import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import useForm from "react-hook-form";
import { Form, Divider, Header } from "semantic-ui-react";
import { LabeledSelect } from "../forms";
import { HOST_URL } from "../../constants";
import { slugify } from "../../utils";
import { projectCreate } from "../../actions/projectActions";

const ProjectCreateForm = ({ closeModal }) => {
  const { register, handleSubmit, errors, setValue, formState } = useForm();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [slug, setSlug] = useState("");
  const [ownerOptions, setOwnerOptions] = useState([]);

  const validateDataset = (file) => {
    const fileExt = file.name.split(".").pop();
    return fileExt === "zip" || "Only zip archives supported";
  };

  useEffect(
    () => {},
    // eslint-disable-next-line
    [isAuthenticated]
  );

  useEffect(
    () => {
      // register form fields
      register({ name: "name" }, { required: "Please enter project name" });
      register(
        { name: "owner" },
        { required: "Please pick the project owner" }
      );
      register(
        { name: "slug" },
        {
          pattern: {
            value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
            message:
              "Slug can contain only lower case letters, digits and '-' symbol.",
          },
        }
      );
      register(
        { name: "ref_speaker" },
        { required: "Please enter reference speaker name" }
      );
      register(
        { name: "src_speaker" },
        { required: "Please enter source speaker name" }
      );
      register(
        { name: "dataset" },
        {
          required: "Please select dataset zip file",
          validate: validateDataset,
        }
      );

      // set default values
      setValue("owner", user.id, formState.isSubmitted);
      const opt = [
        { key: "user", text: <b>User</b>, disabled: true },
        { key: user.id, value: user.id, text: user.name },
        { key: "groups", text: <b>Groups</b>, disabled: true },
      ];
      user.groups.forEach((group) => {
        opt.push({
          key: group.id,
          value: group.id,
          text: group.name,
        });
      });

      setOwnerOptions(opt);
    },
    // eslint-disable-next-line
    []
  );

  const onNameChange = (value) => {
    setValue("name", value, formState.isSubmitted);
    const slug = slugify(value);
    setValue("slug", slug, formState.isSubmitted);
    setSlug(slug);
  };

  const onSlugChange = (value) => {
    setSlug(value);
    setValue("slug", value, formState.isSubmitted);
  };

  const onSubmit = (data) => {
    const ownerName = ownerOptions.find((op) => op.value === data.owner).text;
    dispatch(
      projectCreate({
        name: data.name,
        owner: data.owner,
        slug: data.slug,
        url: `/${slugify(ownerName)}/${data.slug}`,
        ref_speaker: data.ref_speaker,
        src_speaker: data.src_speaker,
        dataset: data.dataset,
      })
    );
    closeModal();
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Header as="h3" content="Project Info" />
      <Form.Input
        name="name"
        label="Project name"
        placeholder="Project name"
        required
        width="8"
        onChange={(e, { value }) => onNameChange(value)}
        error={errors.name ? errors.name.message : null}
      />
      <Form.Group widths="equal">
        <Form.Field>
          <label>Project URL</label>
          <LabeledSelect
            name="owner"
            label={`${HOST_URL}/`}
            fluid
            options={ownerOptions}
            defaultValue={user.id}
            onChange={(e, { value }) =>
              setValue("owner", value, formState.isSubmitted)
            }
            error={errors.owner ? errors.name.message : null}
          />
        </Form.Field>
        <Form.Input
          name="slug"
          required
          label="Project slug"
          placeholder="my-awesome-project"
          value={slug}
          onChange={(e, { value }) => onSlugChange(value)}
          error={errors.slug ? errors.slug.message : null}
        />
      </Form.Group>
      <Divider />
      <Form.Input
        name="ref_speaker"
        required
        label="Reference speaker name"
        placeholder="Reference speaker name"
        onChange={(e) => setValue("ref_speaker", e.target.value, true)}
        error={errors.ref_speaker ? errors.ref_speaker.message : null}
      />
      <Form.Input
        name="src_speaker"
        required
        label="Source speaker name"
        placeholder="Source speaker name"
        onChange={(e) => setValue("src_speaker", e.target.value, true)}
        error={errors.src_speaker ? errors.ref_speaker.message : null}
      />
      <Divider />
      <Header as="h3" content="Dataset" />
      <Header
        as="h6"
        content="Dataset must bee a '.zip' archive. If you have text reference please place it in 'text.csv' file in the root of the archive"
      />
      <Form.Input
        required
        type="file"
        name="dataset"
        accept=".zip"
        onChange={(e) => setValue("dataset", e.target.files[0], true)}
        error={errors.dataset ? errors.dataset.message : null}
      />
      <Form.Button color="green">Create</Form.Button>
    </Form>
  );
};

ProjectCreateForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ProjectCreateForm;
